import moment, { type Moment } from 'moment'

// Action Types
const NEXT_DAY = 'QUEUE_TIMES_NEXT_DAY'
const PREV_DAY = 'QUEUE_TIMES_PREV_DAY'
const SELECT_DAY = 'QUEUE_TIMES_SELECT_DAY'
const GET_TIMES_FOR_DAY = 'QUEUE_TIMES_GET_TIMES_FOR_DAY'

export type QueueTimesApiDayData = {
  openingTime: {
    from: string
    to: string
  }
  gaps: {
    gapId: number
    gapTime: string
    waitingMin: number
  }[]
  waitingTime: {
    from: string
    to: string
  }
  bestTime: string
}
type QueueTimesApiData = Record<string, QueueTimesApiDayData>

export interface QueueTimesState {
  day: Moment
  selected?: Moment
  timesByDay: Record<string, unknown>
  events: Record<string, unknown>
}

interface RootState {
  queueTimes: QueueTimesState
}

type PlainAction = { type: string; [key: string]: unknown }
type GetState = () => RootState
type ThunkAction = (dispatch: Dispatch, getState: GetState) => unknown
type Dispatch = (action: PlainAction | ThunkAction) => unknown

type SelectDayAction = {
  type: typeof SELECT_DAY
  day: Moment
}

type GetTimesFulfilledAction = {
  type: `${typeof GET_TIMES_FOR_DAY}_FULFILLED`
  payload: {
    day: Moment
    data: QueueTimesApiData
  }
}

type QueueTimesAction = SelectDayAction | GetTimesFulfilledAction | PlainAction

const isSelectDayAction = (
  action: QueueTimesAction,
): action is SelectDayAction => {
  return action.type === SELECT_DAY && moment.isMoment(action.day)
}

// Action Creators
export const nextDay = (): ThunkAction => {
  return (dispatch, getState) => {
    dispatch(getTimesForDay(getState().queueTimes.day.clone().add(2, 'day')))
    dispatch({ type: NEXT_DAY })
  }
}

export const prevDay = (): ThunkAction => {
  return (dispatch, getState) => {
    dispatch(
      getTimesForDay(getState().queueTimes.day.clone().subtract(2, 'day')),
    )
    dispatch({ type: PREV_DAY })
  }
}

export const selectDay = (day: Moment): SelectDayAction => {
  return {
    type: SELECT_DAY,
    day,
  }
}

export const getTimesForDay = (day: Moment): ThunkAction => {
  return (dispatch, getState) => {
    if (!(day.format('YYYY-MM-DD') in getState().queueTimes.timesByDay)) {
      dispatch({
        type: GET_TIMES_FOR_DAY,
        payload: new Promise((resolve, reject) => {
          fetch(
            `https://api.miniatur-wunderland.de/v1/days/${day.format('YYYY-MM-DD')}`,
            {
              method: 'GET',
              mode: 'cors',
            },
          )
            .then((response) => {
              response
                .json()
                .then((data: QueueTimesApiData) => {
                  resolve({ day: day.clone(), data })
                })
                .catch(() => {
                  reject(
                    new Error(
                      'Es ist ein Fehler aufgetreten, bitte versuchen Sie es erneut.',
                    ),
                  )
                })
            })
            .catch((error) => {
              console.error(error)
              reject(
                new Error(
                  'Es ist ein Fehler aufgetreten, bitte versuchen Sie es erneut.',
                ),
              )
            })
        }),
      })
    }
  }
}

// Reducer
const initialState: QueueTimesState = {
  day: moment(),
  timesByDay: {},
  events: {},
}

export default function queueTimesReducer(
  state: QueueTimesState = initialState,
  action: QueueTimesAction,
): QueueTimesState {
  switch (action.type) {
    case NEXT_DAY:
      return Object.assign({}, state, {
        day: state.day.clone().add(1, 'day'),
      })

    case PREV_DAY:
      return Object.assign({}, state, {
        day: state.day.clone().subtract(1, 'day'),
      })

    case SELECT_DAY:
      if (isSelectDayAction(action) && action.day.isValid()) {
        return Object.assign({}, state, {
          selected: action.day.clone(),
          day: action.day.clone(),
        })
      }
      return state

    case GET_TIMES_FOR_DAY + '_PENDING':
      return Object.assign({}, state, {})

    case GET_TIMES_FOR_DAY + '_REJECTED':
      return Object.assign({}, state, {})

    case GET_TIMES_FOR_DAY + '_FULFILLED': {
      const fulfilledAction = action as GetTimesFulfilledAction
      const dayKey = fulfilledAction.payload.day.format('YYYY-MM-DD')
      return Object.assign({}, state, {
        timesByDay: Object.assign({}, state.timesByDay, {
          [`${dayKey}`]: fulfilledAction.payload.data[dayKey],
        }),
      })
    }

    default:
      return state
  }
}
